import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Services from "../Services/WebService";
import Paginations from "../Customs/pagination/Paginations";
import NoDataAvailable from "../Customs/NoDataAvailable/NoDataAvailable";
import { FaSave, FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserInfo } from "../Services/Slices/authSlice";
import { AppDispatch, RootState } from "../Services/Store";
import "./AccessManagement.css";

interface UserItem {
  user_id: number;
  username: string;
  email?: string;
  mobile_number: string;
  role_name: string;
  user_image?: string;
}

interface RoleItem {
  role_id: number;
  role_name: string;
}

const usersPerPage = 10;

const AccessManagement: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRoles, setSelectedRoles] = useState<{ [user_id: number]: string }>({});
  const [search, setSearch] = useState<string>("");
  const [searchDebounced, setSearchDebounced] = useState(search);

  // --- RBAC: get access permissions from Redux ---
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const moduleAccess = accessList.find((item: any) => item.code === "rba_control");
  const hasReadAccess = moduleAccess?.read === true;
  const hasWriteAccess = moduleAccess?.write === true;
  const hasUpdateAccess = moduleAccess?.update === true;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const loggedInUserId = useSelector((state: RootState) => state.auth.user?.user_id);

  // --- Debounced search ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchDebounced(search);
    }, 400); 

    return () => clearTimeout(handler);
  }, [search]);

  // --- Fetch roles ---
  const fetchRoles = () => {
    Services.getAPI<any>({ action: `/v1/door-management/rbac/roles` })
      .then((res) => {
        let arr: RoleItem[] = [];
        if (Array.isArray(res)) {
          arr = res;
        } else if (Array.isArray(res.data)) {
          arr = res.data;
        } else if (Array.isArray(res.roles)) {
          arr = res.roles;
        }
        setRoles(arr);
      })
      .catch(() => {
        setRoles([]);
      });
  };

  useEffect(() => {
    fetchRoles();
  }, []);


  const fetchUsers = (page = 1, searchText = "") => {
    setIsLoading(true);
    setErrorMsg("");
    const searchParam = searchText ? `&user_like=${encodeURIComponent(searchText)}` : "";
    Services.getAPI<any>({
      action: `/v1/door-management/users?page_number=${page}&limit=${usersPerPage}${searchParam}`,
    })
      .then((res) => {
        const arr: UserItem[] = Array.isArray(res?.data?.users) ? res.data.users : [];
        setUsers(arr);
        setTotalUsers(res?.data?.pagination?.total_records || arr.length || 0);

        const rolesMap: { [user_id: number]: string } = {};
        arr.forEach((user) => {
          rolesMap[user.user_id] = user.role_name;
        });
        setSelectedRoles(rolesMap);

        if (!arr.length) {
          setErrorMsg("No users found.");
        }
      })
      .catch(() => {
        setUsers([]);
        setErrorMsg("Failed to fetch users.");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchUsers(currentPage, searchDebounced);
    // eslint-disable-next-line
  }, [currentPage, searchDebounced]);

  const handleRoleChange = (user_id: number, role_name: string) => {
    setSelectedRoles((prev) => ({ ...prev, [user_id]: role_name }));
  };

const handleSave = async (user_id: number) => {
  const newRoleName = selectedRoles[user_id];
  if (!newRoleName) {
    toast.error("Please select a valid role first.");
    return;
  }

  const chosenRole = roles.find((r) => r.role_name === newRoleName);
  if (!chosenRole) {
    toast.error("Selected role not found in the list.");
    return;
  }
  const role_id = chosenRole.role_id;


  const userObj = users.find(u => u.user_id === user_id);
  const usernameDisplay = userObj?.username || `ID ${user_id}`;

  try {
    const res: any = await Services.putAPI({
      action: `/v1/door-management/rbac/assign/role`,
      body: {
        user_id: user_id,
        role_id: role_id,
      },
    });

    if (res?.status_code === 200) {
      toast.success(`Role updated to “${newRoleName}” for  “${usernameDisplay}”`);
      fetchUsers(currentPage, searchDebounced);

      if (user_id === loggedInUserId) {
        dispatch(fetchUserInfo());
        toast.info("Your role was changed. The app will reload for updated permissions.");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } else {
      toast.error(res?.message || "Failed to assign role. Please try again.");
    }
  } catch (err: any) {
    const msg =
      typeof err === "string"
        ? err
        : err?.message || "Network error. Please try again.";
    toast.error(msg);
  }
};

  const filteredUsers = users;

  // --- Only render after all hooks have been called ---
  if (!hasReadAccess) {
    return (
      <div style={{ padding: "32px", textAlign: "center", color: "#d32f2f", fontWeight: 700, fontSize: 22 }}>
        Access Denied: You do not have permission to view Access Management.
      </div>
    );
  }

  return (
    <div className="access-mgmt-page">
      <div className="mgmt-tabs">
        <button
          className="mgmt-tab"
          onClick={() => navigate("/role-management")}
        >
          <span className="mgmt-tab-icon">🗂️</span> Role Management
        </button>
        <button className="mgmt-tab active">
          <span className="mgmt-tab-icon">🔑</span> Access Management
        </button>
        <button
          className="mgmt-tab"
          onClick={() => navigate("/state-city")}
        >
          <span className="mgmt-tab-icon">📍</span> State & City
        </button>
      </div>

      <h2 className="panel-title access-title">Role Management</h2>
      <p className="panel-desc">
        Create and manage user roles. Edit roles as needed for your organization.
      </p>

      <div className="search-row">
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="access-mgmt-table-container">
        {isLoading ? (
          <NoDataAvailable isShowLoader={true} />
        ) : errorMsg ? (
          <NoDataAvailable
            message={errorMsg}
            isShowNoDataAvailable={true}
            isShowLoader={false}
          />
        ) : filteredUsers.length === 0 ? (
          <NoDataAvailable
            message="No users found"
            isShowNoDataAvailable={true}
            isShowLoader={false}
          />
        ) : (
          <table className="access-mgmt-table">
            <thead>
              <tr style={{ background: "#FFF4C3" }}>
                <th>Name</th>
                <th>Mobile number</th>
                <th>Assign role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.user_id}>
                  <td>
                    <div className="user-cell">
                      <img
                        className="user-avatar"
                        src={
                          user.user_image ||
                          `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
                            user.username
                          )}`
                        }
                        alt={user.username}
                      />
                      <div className="user-details">
                        <span className="user-name">{user.username}</span>
                        {user.email && (
                          <span className="user-email">{user.email}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{user.mobile_number}</td>
                  <td>
                    <select
                      className="role-dropdown"
                      value={selectedRoles[user.user_id] || user.role_name}
                      onChange={(e) =>
                        handleRoleChange(user.user_id, e.target.value)
                      }
                      disabled={!(hasWriteAccess || hasUpdateAccess)}
                    >
                      {roles.map((role) => (
                        <option
                          key={role.role_id}
                          value={role.role_name}
                        >
                          {role.role_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className="save-btn"
                      onClick={() => handleSave(user.user_id)}
                      disabled={!(hasWriteAccess || hasUpdateAccess)}
                    >
                      <FaSave style={{ marginRight: 6 }} /> Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && users.length > 0 && (
        <Paginations
          totalItems={totalUsers}
          itemsCountPerPage={usersPerPage}
          changePage={setCurrentPage}
          changeCurrentPage={currentPage}
        />
      )}
    </div>
  );
};

export default AccessManagement;
